import { BASE_URL } from "@/constants/endpoints";
import { LegalOnboardingStatusIds } from "@/constants/enums";
import { LegalOnboardingDetail } from "@/pages/onboarding/legal/detail/schema/handler-functions";
import { createAxiosWithAuth } from "@/utils/axios-config";
import { objectToQueryStringWithArray } from "@/utils/query-string";
import { AxiosInstance } from "axios";

let legalOnboardingApiPromise: Promise<AxiosInstance> | null = null;

const getLegalOnboardingApi = async (): Promise<AxiosInstance | null> => {
  if (legalOnboardingApiPromise) return legalOnboardingApiPromise;
  legalOnboardingApiPromise = createAxiosWithAuth(BASE_URL, "jwt");
  return legalOnboardingApiPromise;
};

export interface PaginationProps {
  "P.Pagina": number;
  "P.Records"?: number;
}

export interface LegalOnboardingListItem {
  id: number;
  cuitCuil: string;
  tipoSociedad: string;
  estado: string;
}

export interface GetLegalOnboardingRequestsProps extends PaginationProps {
  cuitCuil?: string;
  tipoSociedad?: number[];
  estado?: number[];
}

export const getLegalOnboardingRequests = async (props: GetLegalOnboardingRequestsProps) => {
  const api = await getLegalOnboardingApi();
  if (!api) return null;

  const query = {
    Cuit: props?.cuitCuil,
    Estado: props?.estado?.length ? props.estado : undefined,
    TipoSociedad: props?.tipoSociedad?.length ? props.tipoSociedad : undefined,
    "P.Pagina": props?.["P.Pagina"] ?? 1,
    "P.Records": props?.["P.Records"] ?? 10,
  };

  const response = await api.get(
    `/OnboardingJuridico?${objectToQueryStringWithArray(query)}`,
  );

  return response.data;
};

interface GetLegalOnboardingRequestDetailProps {
  id: number;
}

export const getLegalOnboardingRequestDetail = async (
  props: GetLegalOnboardingRequestDetailProps,
): Promise<LegalOnboardingDetail | null> => {
  const api = await getLegalOnboardingApi();
  if (!api) return null;

  const response = await api.get(`/OnboardingJuridico/${props.id}`);
  return response.data;
};

export interface UpdateLegalOnboardingDocumentoBody {
  Id: number;
  Estado: number;
}

export interface UpdateLegalOnboardingRequestBody {
  Id: number;
  IdEstado: number;
  Observacion?: string;
  Documentos?: UpdateLegalOnboardingDocumentoBody[];
  datosCvu?: {
    cuit: string;
    alias: string;
    cvu: string;
    idCuenta: number;
    idCvu: number;
  };
}

export const updateLegalOnboardingRequest = async (
  idSolicitud: number,
  body: UpdateLegalOnboardingRequestBody,
) => {
  const api = await getLegalOnboardingApi();
  if (!api) return null;

  const response = await api.patch(`/OnboardingJuridico/${idSolicitud}`, body);
  return response.data;
};

export interface CreateLegalOnboardingCvuRequestBody {
  idSolicitud: number;
  cuit: string;
  razonSocial: string;
  tipoPersona: PersonType;
  domicilio: {
    calle: string;
    numero: string;
    piso: string;
    localidadId: number;
    provinciaId: number;
    codigoPostal: string;
  };
}

export interface CreateLegalOnboardingCvuResponse {
  accountId: number;
  cvuId: number;
  cvuAlias: string;
  cvu: string;
}

export interface BindAccountCreationResponse extends CreateLegalOnboardingCvuResponse {
  cuitCuil: string;
}

export const createLegalOnboardingAccountInBind = async (
  body: CreateLegalOnboardingCvuRequestBody & { cuitCuil: string },
): Promise<BindAccountCreationResponse> => {
  const api = await getLegalOnboardingApi();
  if (!api) throw new Error("API no disponible");

  const response = await api.post(
    `/OnboardingJuridico/crear-cuenta-y-cvu`,
    {
      idSolicitud: body.idSolicitud,
      cuit: body.cuit,
      razonSocial: body.razonSocial,
      tipoPersona: PersonType.LegalEntity,
      domicilio: body.domicilio,
    } satisfies CreateLegalOnboardingCvuRequestBody,
  );

  return {
    ...(response.data as CreateLegalOnboardingCvuResponse),
    cuitCuil: body.cuitCuil,
  };
};

export enum PersonType {
  Individual = 1,
  LegalEntity = 2
}

export const confirmApproveLegalOnboarding = async (
  idSolicitud: number,
  documentos?: UpdateLegalOnboardingDocumentoBody[],
  datosCvu?: {
    cuit: string;
    alias: string;
    cvu: string;
    idCuenta: number;
    idCvu: number;
  },
) => {
  return await updateLegalOnboardingRequest(idSolicitud, {
    Id: idSolicitud,
    IdEstado: LegalOnboardingStatusIds.Aprobado,
    Documentos: documentos,
    datosCvu,
  });
};